import { useNavigate, useSearchParams } from "react-router-dom"

import Icon from "@/components/Icon"
import ReconcileSummary from "@/components/incentives/ReconcileSummary"
import ReconciledIncentiveCard from "@/components/incentives/ReconciledIncentiveCard"
import { formatWeek, thisWeek, weekFrom } from "@/components/incentives/reconcileWeek"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import { useIncentiveReconcile } from "@/hooks/useIncentives"

import { addDaysToDateString } from "../../shared/shift-timing.ts"
import DashboardLayout from "../layouts/DashboardLayout"

export default function IncentiveReconcile() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const week = weekFrom(searchParams.get("week"))
  const { data: incentives, isLoading, isFetching, error, refetch } = useIncentiveReconcile(week)

  const moveWeek = (days: number) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set("week", addDaysToDateString(week, days))
        return next
      },
      { replace: true },
    )

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <Button
          variant="ghost"
          className="-ml-4 flex items-center gap-2"
          onClick={() => navigate("/sheriff?tab=incentives")}
        >
          <Icon name="arrowLeft" className="h-4 w-4 text-current" />
          Back to incentives
        </Button>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-semibold tracking-tight text-palette-jungleGreen900">
            Reconcile incentives
          </h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              aria-label="Previous week"
              onClick={() => moveWeek(-7)}
            >
              <Icon name="caretLeft" className="h-4 w-4 text-current" />
            </Button>
            <span className="min-w-[220px] text-center text-sm font-medium text-palette-jungleGreen900">
              {formatWeek(week)}
            </span>
            <Button
              variant="outline"
              size="icon"
              aria-label="Next week"
              disabled={week >= thisWeek()}
              onClick={() => moveWeek(7)}
            >
              <Icon name="caretRight" className="h-4 w-4 text-current" />
            </Button>
          </div>
        </div>

        {week === thisWeek() && (
          <Alert>
            <Icon name="infoCircle" className="h-4 w-4" />
            <AlertDescription>
              This week isn't over yet, so the counts and amounts will still change.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <Icon name="warningCircleFilled" className="h-4 w-4" />
            <AlertDescription className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-medium">Couldn't reconcile this week. Try again.</p>
                <p className="text-xs">
                  {error instanceof Error ? error.message : "Unknown error"}
                </p>
              </div>
              <Button variant="outline" size="sm" disabled={isFetching} onClick={() => refetch()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
            <span className="ml-2 text-palette-jungleGreen700">Reconciling the week...</span>
          </div>
        )}

        {incentives?.length === 0 && (
          <Card>
            <CardContent className="p-6 text-sm text-palette-jungleGreen600">
              No live or ended incentive ran in this week.
            </CardContent>
          </Card>
        )}

        {incentives?.length > 0 && <ReconcileSummary incentives={incentives} />}

        {incentives?.map((incentive) => (
          <ReconciledIncentiveCard
            key={`${incentive.incentive_id}-${week}`}
            incentive={incentive}
            week={week}
          />
        ))}
      </div>
    </DashboardLayout>
  )
}
